import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, Clock, XCircle } from "lucide-react";
import {
  useGetFirmApplicationDetailsQuery,
  useFirmReopenMutation,
  useFirmResubmitMutation,
  useInitiateFirmDonationMutation,
} from "../api/firmApi";
import { redirectToOnePay } from "../utils";
import FirmStatusBadge from "../components/FirmStatusBadge";
import FirmPersonCard from "../components/FirmPersonCard";
import { firmStorageUrl } from "../utils";

/** A stored date, as a reader wants it. Blank stays blank. */
function fmtDate(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-US", { dateStyle: "medium" });
}

const naira = (kobo?: number | null) =>
  kobo == null ? null : `₦${(Number(kobo) / 100).toLocaleString()}`;

const partnerDecision: Record<string, JSX.Element> = {
  accepted: <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> accepted</span>,
  pending: <span className="text-amber-600 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> pending</span>,
  declined: <span className="text-red-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> declined</span>,
  approved: <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> approved</span>,
  rejected: <span className="text-red-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> rejected</span>,
};

function Row({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{String(value)}</span>
    </div>
  );
}

export default function FirmApplicationDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: app, isLoading } = useGetFirmApplicationDetailsQuery(id!, { skip: !id });
  const [reopen, { isLoading: reopening }] = useFirmReopenMutation();
  const [resubmit, { isLoading: resubmitting }] = useFirmResubmitMutation();
  const [payDonation, { isLoading: donating }] = useInitiateFirmDonationMutation();
  const [payError, setPayError] = useState<string | null>(null);

  if (isLoading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!app) return <div className="p-8 text-center text-gray-400">Application not found.</div>;

  // The roster in declaration order, principal first — the same order step 1
  // presented, so the page reads back exactly what was filled in.
  const ORDER: Record<string, number> = { principal: 0, partner: 1, director: 2 };
  const partners = [...(app.partners ?? [])].sort(
    (a: any, b: any) => (ORDER[a.role] ?? 9) - (ORDER[b.role] ?? 9),
  );
  const editable = ["draft", "pending_payment", "returned"].includes(app.status);
  // Paid and with the secretariat, but the chapter hasn't endorsed yet — the
  // applicant has to go and ask them, so say so plainly and name who to ask.
  const awaitingEndorsement = app.status === "awaiting_endorsement";
  const approvedReferees = (app.referees ?? []).filter((r: any) => r.decision === "approved").length;

  const startDonation = async () => {
    setPayError(null);
    try {
      redirectToOnePay(await payDonation().unwrap());
    } catch (e: any) {
      setPayError(e?.data?.message ?? "Could not start the donation payment. Please try again.");
    }
  };

  return (
    <div className="w-full p-4 md:p-6 max-w-3xl mx-auto">
      <Link to="/firm-applications" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{app.practice_name}</h1>
          <p className="text-xs text-gray-400">#{String(app.id).padStart(6, "0")}</p>
        </div>
        <FirmStatusBadge status={app.status} />
      </div>

      {app.firm && (
        <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-200">
          <p className="text-sm font-semibold text-green-800">Firm registered</p>
          <p className="text-sm text-green-700">
            {app.firm.name} — firm no. <span className="font-mono font-semibold">{app.firm.firm_no}</span>
          </p>
        </div>
      )}

      {awaitingEndorsement && (
        <div className="mb-4 p-4 rounded-xl bg-indigo-50 border border-indigo-200">
          <p className="text-sm font-semibold text-indigo-900">Waiting on endorsements</p>
          <p className="text-sm text-indigo-800 mt-0.5">
            Payment is done. Your application goes to the secretariat once both referees have
            approved it and your chapter has endorsed it.
          </p>
          <ul className="text-sm text-indigo-800 mt-2 space-y-0.5">
            <li>
              {approvedReferees >= 2 ? "✓" : "•"} Referee approvals ({approvedReferees}/2)
            </li>
            <li>
              {app.chapter_accepted_at ? "✓" : "•"} Chapter endorsement
              {!app.chapter_accepted_at && (
                <>
                  {" "}— reach out to
                  {app.chapter?.name ? <> <span className="font-medium">{app.chapter.name}</span></> : " your chapter"};
                  it is waiting for them on the chapter portal.
                </>
              )}
            </li>
          </ul>
          {!app.chapter_accepted_at && (app.chapter_chairman_name || app.chapter_secretary_name) && (
            <p className="text-xs text-indigo-700 mt-2">
              {[
                app.chapter_chairman_name && `Chairman: ${app.chapter_chairman_name}${app.chapter_chairman_email ? ` (${app.chapter_chairman_email})` : ""}`,
                app.chapter_secretary_name && `Secretary: ${app.chapter_secretary_name}${app.chapter_secretary_email ? ` (${app.chapter_secretary_email})` : ""}`,
              ].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      )}

      {(app.status === "under_review" || app.status === "with_committee") && (
        <div className="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm text-indigo-800">
          All endorsements are in.{" "}
          {app.status === "with_committee"
            ? "The Membership Committee is reviewing your application"
            : "The secretariat is reviewing your application"}{" "}
          — there is nothing for you to do right now.
        </div>
      )}

      {app.status === "rejected" && (
        <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200">
          <p className="text-sm font-semibold text-red-900">Returned to the secretariat by the committee</p>
          {app.remarks && <p className="text-sm text-red-800 mt-0.5">{app.remarks}</p>}
          <p className="text-sm text-red-800 mt-1">
            The secretariat will decide whether to send it back to you to correct.
          </p>
        </div>
      )}

      {app.status === "pending_donation" && (
        <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-sm font-semibold text-amber-900">Interview done — donation outstanding</p>
          <p className="text-sm text-amber-800 mt-0.5">
            Pay the donation agreed at your interview
            {app.donation_amount != null && (
              <> of <span className="font-semibold">₦{(Number(app.donation_amount) / 100).toLocaleString()}</span></>
            )} — your firm number is issued once it is received.
          </p>
          {payError && <p className="text-sm text-red-700 mt-2">{payError}</p>}
          <button
            onClick={startDonation}
            disabled={donating}
            className="mt-3 px-5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60"
          >
            {donating ? "Starting payment…" : "Pay donation"}
          </button>
        </div>
      )}

      {app.status === "ready_for_interview" && (
        <div className="mb-4 p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-800">
          <span className="font-medium">Ready for interview.</span> The Membership Committee has
          approved your application — the secretariat will be in touch about scheduling. The
          donation is agreed at the interview, and you will be asked to pay it afterwards.
        </div>
      )}

      {app.status === "donated" && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
          <span className="font-medium">Donation received.</span> Nothing further is needed from you —
          the secretariat issues your firm number, which registers the practice.
        </div>
      )}

      {app.chapter_accepted_at && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
          <span className="font-medium">Endorsed by your chapter.</span>
          {app.chapter_accept_comment ? ` ${app.chapter_accept_comment}` : ""}
        </div>
      )}

      {app.status === "returned" && app.remarks && (
        <div className="mb-4 p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800">
          <p className="font-medium">Returned for correction</p>
          <p>{app.remarks}</p>
        </div>
      )}

      {editable && (
        <div className="mb-4 flex gap-2">
          {app.status === "returned" && (
            <button onClick={async () => { await reopen({ id: app.id }).unwrap(); navigate("/firm-applications/new"); }}
              disabled={reopening} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm disabled:opacity-60">
              {reopening ? "Reopening…" : "Reopen & edit"}
            </button>
          )}
          {app.payment_id && ["draft", "returned"].includes(app.status) && (
            <button onClick={() => resubmit({ id: app.id })} disabled={resubmitting}
              className="px-4 py-2 rounded-lg border text-sm text-gray-700 disabled:opacity-60">
              {resubmitting ? "Resubmitting…" : "Resubmit (already paid)"}
            </button>
          )}
          {["draft", "pending_payment"].includes(app.status) && (
            <Link to="/firm-applications/new" className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm">Continue</Link>
          )}
        </div>
      )}

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Firm details</h3>
        <Row label="Practice name" value={app.practice_name} />
        <Row label="Nature of practice" value={app.nature_of_practice} />
        <Row label="Registered address" value={app.registered_address} />
        <Row label="Firm email" value={app.email} />
        <Row label="Firm phone number" value={app.phone} />
        <Row label="State" value={app.state?.name ?? (typeof app.state === "string" ? app.state : null)} />
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">
          Registration under the Companies and Allied Matters Act or the Registration of Business Name Act
        </h3>
        <Row label="CAC registration number" value={app.cama_reg_number} />
        <Row label="Date of registration with CAC" value={fmtDate(app.cac_reg_date)} />
        <Row label="Date of commencement of practice" value={fmtDate(app.commencement_date)} />
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Ownership structure</h3>
        <Row label="Structure" value={app.ownership_structure} />
        <Row label="Number of partners / directors" value={app.num_partners} />
        {(app.affiliations ?? []).length > 0 && (
          <div className="pt-2">
            <p className="text-sm text-gray-500 mb-1">Affiliations in Nigeria or abroad</p>
            <ul className="text-sm text-gray-900 space-y-0.5">
              {(app.affiliations ?? []).map((a: string, i: number) => <li key={i}>• {a}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-1">Partners &amp; directors</h3>
        {partners.length === 0 && <p className="text-sm text-gray-400 mt-2">None listed.</p>}
        {partners.map((p: any) => <FirmPersonCard key={p.id} p={p} />)}
      </div>

      {(app.benevolence_records ?? []).length > 0 && (
        <div className="bg-white border rounded-xl p-5 mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">Acts of benevolence to NIQS</h3>
          {(app.benevolence_records ?? []).map((b: any) => (
            <div key={b.id} className="py-2 border-b last:border-0">
              {b.type && <p className="text-xs font-medium text-indigo-600">{b.type}</p>}
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{b.description}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Referees</h3>
        {(app.referees ?? []).length === 0 && <p className="text-sm text-gray-400">None yet.</p>}
        {(app.referees ?? []).map((r: any) => (
          <div key={r.id} className="flex items-center justify-between py-2 border-b last:border-0">
            <div>
              <p className="text-sm text-gray-900">
                {[r.member?.surname, r.member?.firstname, r.member?.othernames].filter(Boolean).join(" ") || r.member_id}
              </p>
              <p className="text-xs text-gray-400">{r.member?.membership_no}</p>
            </div>
            {partnerDecision[r.decision]}
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-2">Your chapter</h3>
        <Row label="Chapter" value={app.chapter?.name ?? app.state_chapter} />
        <Row label="Chairman" value={app.chapter_chairman_name} />
        <Row label="Chairman email" value={app.chapter_chairman_email} />
        <Row label="Chairman phone" value={app.chapter_chairman_phone} />
        <Row label="Secretary" value={app.chapter_secretary_name} />
        <Row label="Secretary email" value={app.chapter_secretary_email} />
        <Row label="Secretary phone" value={app.chapter_secretary_phone} />
        <Row label="Endorsed" value={fmtDate(app.chapter_accepted_at)} />
        <Row label="Endorsement comment" value={app.chapter_accept_comment} />
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-3">Registration documents</h3>
        {(app.documents ?? []).length === 0 && <p className="text-sm text-gray-400">None uploaded.</p>}
        {(app.documents ?? []).map((d: any) => {
          const url = firmStorageUrl(d.file_path);
          return (
            <div key={d.id} className="flex items-center justify-between py-2 border-b last:border-0">
              <div className="min-w-0">
                <p className="text-sm text-gray-900">{d.doc_type}</p>
                <p className="text-xs text-gray-400 truncate">{d.file_name}</p>
              </div>
              {url && <a href={url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline shrink-0">View</a>}
            </div>
          );
        })}
      </div>

      <div className="bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-2">Declaration &amp; payment</h3>
        <Row label="Declaration acknowledged" value={fmtDate(app.acknowledged_at)} />
        <Row label="Registration fee" value={naira(app.amount)} />
        <Row label="Payment status" value={app.payment?.status} />
        <Row label="Payment reference" value={app.reference} />
        <Row label="Receipt no." value={app.payment?.receipt_number} />
        <Row label="Invoice no." value={app.invoice?.invoice_number} />
        <Row label="Donation" value={naira(app.donation_amount)} />
        <Row label="Donation paid" value={fmtDate(app.donation_paid_at)} />
        <Row label="Submitted" value={fmtDate(app.created_at)} />
      </div>
    </div>
  );
}
